<script lang="ts">
	import {
		List,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		DescriptionList,
		Heading,
		Hr,
		Img,
		P,
		Popover
	} from 'flowbite-svelte';
	import { QuestionCircleSolid, ChevronRightOutline } from 'flowbite-svelte-icons';
	import DynamicContent from '$lib/components/DynamicContent.svelte';
	import { formatNumber, formatCurrency, formatDate, formatPercentage } from '$lib/formatting.js';

	const getQualityString = (score: number) => {
		if (score > 90) return 'one of the best scratchoffs in the country';
		if (score > 75) return 'a better than average scratchoff';
		if (score > 60) return 'a worse than average scratchoff';
		if (score > 0 || !score) return 'one of the worst scratchoffs in the country';
	};

	export let data;
</script>

{#if data.game}
	<Heading tag="h2" class="text-center mb-8"
		>{data?.gameData?.name} #{data?.game?.game_id} | 🏆{Math.round(data?.gameData?.score)}<button
			id="info-btn"
		>
			<QuestionCircleSolid class="w-5 h-5 ms-1.5" />
			<span class="sr-only">Show information about scores</span>
		</button>
	</Heading>
{:else}
	<Heading tag="h2" class="text-center mb-8">Game not found</Heading>
{/if}
<Popover
	triggeredBy="#info-btn"
	class="w-72 text-sm font-light text-gray-500 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
	placement="bottom-start"
>
	<div class="p-3 space-y-2">
		<h3 class="font-semibold text-gray-900 dark:text-white">Basic Score</h3>
		This number represents the profitability of a game. The higher, the better. Over 100 is profitable.
		<h3 class="font-semibold text-gray-900 dark:text-white">How do we calculate Basic Score?</h3>
		(&lt;cost of remaining tix&gt; / &lt;value of remaining prizes&gt; * 100). This represents the "expected
		value". Expected value is similar to "return on investment".
		<a
			href="/articles"
			class="flex items-center font-medium text-primary-600 dark:text-primary-500 dark:hover:text-primary-600 hover:text-primary-700"
		>
			Read more <ChevronRightOutline
				class="w-4 h-4 ms-1.5 text-primary-600 dark:text-primary-500"
			/>
		</a>
	</div>
</Popover>
<Hr class="py-8" />
{#if data?.game?.image_urls?.length > 0}
	<div class="grid grid-flow-col auto-cols-fr gap-4 p-4 w-full overflow-x-auto">
		{#each data?.game?.image_urls as image}
			<div class="flex justify-center items-center">
				<Img
					src={`https://d2lzwy9hvmq78v.cloudfront.net/${image}`}
					alt={data?.game?.name}
					class="w-full max-w-[400px] h-auto object-contain"
				/>
			</div>
		{/each}
	</div>
{/if}
{#if data?.game?.description}
	<Heading tag="h2" class="mb-4">Description</Heading>
	<DynamicContent content={data?.game?.description} />
	<Hr class="py-8" />
{/if}
{#if data?.game?.how_to_play}
	<Heading tag="h2" class="mb-4"
		>How to play {data?.gameData?.name} in {data.gameData?.state}</Heading
	>
	<DynamicContent content={data?.game?.how_to_play} />
	<Hr class="py-8" />
{/if}
{#if data?.gameData}
	<Heading tag="h2" class="mb-4">Analysis</Heading>
	<P class="sm:text-lg mb-4">
		{data?.gameData?.name} has a score of {Math.round(data?.gameData?.score)}, making it
		<strong>{getQualityString(data?.gameData?.score)}.</strong>
	</P>
	<P class="sm:text-lg mb-4">
		There are {formatNumber(data?.gameData?.numTxRemaining)} tickets remaining. At {formatCurrency(
			data?.gameData?.price
		)} per ticket, it would cost {formatCurrency(data?.gameData?.costToBuyRemainingTx)} to buy all remaining
		tickets. According to the latest reports from the {data?.gameData?.state} lottery website, there
		are {formatCurrency(data?.gameData?.valueRemainingPrizes)} in remaining prizes.
	</P>
	<P class="sm:text-lg mb-4">
		The value of the remaining prizes is {#if data?.gameData?.prizesExceedingCost > 0}above{:else}below{/if}
		the cost of the remaining tickets by {formatCurrency(
			Math.abs(data?.gameData?.prizesExceedingCost)
		)}. With {formatNumber(data?.gameData?.numTxRemaining)} tickets remaining, that's an average of
		<strong>{formatCurrency(data?.gameData?.valuePerTx, true)} per ticket.</strong></P
	>

	<P class="sm:text-lg mb-4">
		An average of {formatNumber(data?.gameData?.txSoldPerDay)} tickets have been claimed each day between
		{formatDate(data?.gameData?.start)} and {formatDate(data?.gameData?.end)}. The largest prize
		claimed in that time period was {formatCurrency(data?.gameData?.largestPrizeClaimed)}.
	</P>
	<P class="sm:text-lg mb-4">
		The last grand prize of {formatCurrency(data?.gameData?.grandPrizeValue)}
		was claimed on {formatDate(data?.gameData?.grandPrizeLastClaimed)}. {formatNumber(
			data?.gameData?.txSinceLastGrandPrize
		)} tickets have been sold since the last grand prize was claimed. With
		{formatNumber(data?.gameData?.totalNumGrandPrize)} grand prizes in the game,
		<strong
			>it's expected that there will be 1 grand prize sold every {formatNumber(
				data?.gameData?.txPerGrandPrize
			)} tickets.</strong
		>
	</P>
	<Hr class="py-8" />
{/if}
{#if data?.game}
	<List tag="dl" class="divide-y divide-gray-200">
		<div class="grid grid-cols-2 lg:grid-cols-3 gap-4">
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Last Updated</DescriptionList>
				<DescriptionList tag="dd" class="mb-3">{formatDate(data?.game?.updated_at)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Ticket Price</DescriptionList>
				<DescriptionList tag="dd" class="mb-3">{formatCurrency(data?.game?.price)}</DescriptionList>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Ticket Value</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>{formatCurrency(data?.game?.ticket_value)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Ticket Profit</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>{formatCurrency(data?.game.ticket_value - data?.game.price)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Odds of Winning</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>1 in {formatNumber(data?.game?.calculated_odds, 4)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Number Tickets Printed</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>{formatNumber(data?.game?.num_tx_initial)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Number Tickets Remain</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>{formatNumber(data?.game?.num_tx_remain)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Percent Tickets Remain</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>{formatPercentage(1 - data?.game?.percent_tickets_sold, 4)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Initial Expected Value</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>{formatPercentage(data?.game?.expected_value_initial, 4)}</DescriptionList
				>
			</div>
			<div class="flex flex-col pb-3">
				<DescriptionList tag="dt" class="mb-1">Current Expected Value</DescriptionList>
				<DescriptionList tag="dd" class="mb-3"
					>{formatPercentage(data?.game?.expected_value, 4)}</DescriptionList
				>
			</div>
		</div>
	</List>
	<Hr class="py-8" />
	<Heading tag="h2" class="mb-8">Prizes</Heading>
	{#each data?.game?.prizes as prize}
		<div class="mb-8">
			<Heading tag="h2" class="mb-4">
				{prize.prize}
			</Heading>
			<Table striped={true}>
				<TableBody tableBodyClass="w-full sm:text-lg text-gray-500 text-left">
					<TableBodyRow>
						<TableBodyCell>Remaining:</TableBodyCell>
						<TableBodyCell>{formatNumber(prize.available)}</TableBodyCell>
					</TableBodyRow>
					<TableBodyRow>
						<TableBodyCell>Claimed:</TableBodyCell>
						<TableBodyCell>{formatNumber(prize.claimed)}</TableBodyCell>
					</TableBodyRow>
					<TableBodyRow>
						<TableBodyCell>Percent Remaining:</TableBodyCell>
						<TableBodyCell
							>{formatPercentage(
								prize.available / (prize.available + prize.claimed),
								2
							)}</TableBodyCell
						>
					</TableBodyRow>
				</TableBody>
			</Table>
		</div>
	{/each}
{/if}
